import SEO from '../components/SEO'
import StructuredData from '../components/StructuredData'
import Button from '../components/site/Button'
import SectionHeading from '../components/site/SectionHeading'
import CtaBand from '../components/site/CtaBand'
import { identity, contact, career, education, press, selectedProjects } from '../data/profile'
import { personSchema } from '../lib/structuredData'

// About (prototype About.dc.html minus the BRIEF deviations: no logos, no "PhD", Selected
// projects as text per Q18). Facts from src/data/profile.js; "off the clock": sub-2:40 from
// Martin's PRD, 13 years of training and the KULG sentence from TalTech Trialoog 4 Aug 2026
// (BRIEF item 8); quote from Forte 30 Jul 2026.

const section = 'mx-auto max-w-[1180px] px-4 pb-14 sm:px-6'
const mono = 'font-mono text-xs uppercase tracking-wider text-ink/70'
const linkBlock = 'inline-flex min-h-11 items-center text-link underline underline-offset-4'

// Same computation as Home: first analyst role to the end of the last finished role.
const lastEnd = Math.max(...career.filter((c) => c.end).map((c) => Number(c.end.slice(0, 4))))
const analystYears = lastEnd - identity.analystSince
const interview = press.find((p) => p.source === 'forteInterview')

const year = (ym) => ym.slice(0, 4)
const span = (c) => `${year(c.start)}–${c.end ? year(c.end) : ''}`
const shortDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
const eduLabel = (e) => (e.inProgress ? `${e.degree} · in progress` : `${e.degree} · ${e.year}`)

function External({ href, className = 'text-link underline underline-offset-4', children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  )
}

export default function About() {
  return (
    <div className="grid-paper">
      <SEO
        title="About Martin Tamm"
        description="Business analyst turned doctoral researcher at TalTech, Head of Product & AI at KULG. Career, education, selected projects and press."
        path="/about"
        type="profile"
      />
      <StructuredData data={personSchema()} />

      <section className="mx-auto flex max-w-[1180px] flex-col gap-10 px-4 pb-12 pt-12 sm:px-6 sm:pt-16 md:flex-row">
        <aside aria-label="Contact" className="w-full max-w-[360px] shrink-0 md:w-[320px]">
          <img
            src="/martin-headshot.jpg"
            alt="Portrait of Martin Tamm"
            width="900"
            height="1200"
            className="block h-auto w-full border-2 border-ink"
          />
          <ul className="mt-5 flex flex-col">
            <li>
              <a href={`mailto:${contact.email}`} className={linkBlock}>
                {contact.email}
              </a>
            </li>
            <li>
              <External href={contact.linkedin} className={linkBlock}>
                LinkedIn
              </External>
            </li>
            <li>
              <External href={contact.etis} className={linkBlock}>
                ETIS research CV
              </External>
            </li>
          </ul>
          <Button href="/cv.pdf" variant="secondary" className="mt-4">
            Download CV (PDF)
          </Button>
        </aside>

        {/* Text first on phones so the H1 is above the fold; portrait first from md up. */}
        <div className="order-first min-w-0 flex-1 md:order-none">
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-wider text-signal">About</p>
          <h1 className="max-w-3xl font-serif text-4xl font-bold leading-tight text-ink sm:text-5xl">
            From business analysis to research, by way of real systems.
          </h1>
          <p className="mt-6 max-w-2xl text-lg">
            I spent about {analystYears} years as a business analyst in software teams, the last of them alongside
            doctoral research at TalTech. That background is why I care about the gap between a good idea and a system that
            actually works: a requirement fits in one sentence of a regulation, but becomes dozens of data fields,
            interfaces, owners and edge cases in software.
          </p>
          <p className="mt-4 max-w-2xl text-lg">
            Today I split my time between research on Digital Product Passports, leading product and AI at KULG, and
            helping companies build AI systems they can trust.
          </p>
          {interview && (
            <blockquote className="mt-8 max-w-2xl border-y-2 border-ink py-6">
              <p className="font-serif text-[26px] italic leading-snug">
                “When AI says ‘do this’, the next question should always be: based on what data?”
              </p>
              <footer className="mt-3.5 font-mono text-xs text-ink/70">
                — <External href={interview.url}>{interview.outlet}</External>, {shortDate(interview.date)}.
                Translated from Estonian.
              </footer>
            </blockquote>
          )}
        </div>
      </section>

      <section aria-labelledby="about-career" className={section}>
        <SectionHeading id="about-career" number={1}>
          Career and education
        </SectionHeading>
        <div className="mt-5 grid grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))] gap-8">
          <div className="min-w-0">
            <p className={mono} id="about-career-list">
              Career
            </p>
            <ul aria-labelledby="about-career-list" className="mt-3 border-y-2 border-ink">
              {career.map((c) => (
                <li key={c.employer} className="flex gap-4 border-b border-ink/30 py-3 last:border-b-0">
                  <span className="w-24 shrink-0 font-mono text-sm text-ink/70">{span(c)}</span>
                  <span>
                    <b>{c.employer}</b> · {c.role}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="min-w-0">
            <p className={mono} id="about-education-list">
              Education
            </p>
            <ul aria-labelledby="about-education-list" className="mt-3 border-y-2 border-ink">
              {education.map((e) => (
                <li key={`${e.degree}-${e.field ?? ''}`} className="border-b border-ink/30 py-3 last:border-b-0">
                  <span className="block font-mono text-xs uppercase tracking-wider text-ink/70">{eduLabel(e)}</span>
                  <span>
                    {e.institution}
                    {e.field && `, ${e.field.toLowerCase()}`}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 border-2 border-ink bg-white p-5">
              <p className={mono}>Off the clock</p>
              <p className="mt-2">
                Runner with 13 years of training behind me and a sub-2:40 marathon. Running is also how I came to
                KULG: I had built an AI training planner for myself first.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="about-projects" className={section}>
        <SectionHeading id="about-projects" number={2}>
          Selected projects
        </SectionHeading>
        <p className="mt-4 max-w-[760px] text-ink/80">
          Projects I worked on as an employee, with the role I had and the company I worked for.
        </p>
        <ul className="mt-5 flex flex-col gap-2 border-l-4 border-signal pl-5">
          {selectedProjects.map((p) => (
            <li key={`${p.client}-${p.project}`}>
              {p.client}: {p.project}, as {p.role} at {p.employer}
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="about-press" className={section}>
        <SectionHeading id="about-press" number={3}>
          In the press
        </SectionHeading>
        <ul aria-label="In the press" className="mt-5 border-y-2 border-ink">
          {press.map((p) => (
            <li key={p.url} className="border-b border-ink/30 last:border-b-0">
              <External
                href={p.url}
                className="flex min-h-11 flex-wrap items-baseline gap-x-6 gap-y-1 py-3 text-ink no-underline hover:bg-white"
              >
                <span className="w-56 font-mono text-xs uppercase tracking-wider text-ink/70">
                  {p.outlet} · {shortDate(p.date)} · {p.language.toUpperCase()}
                  {p.kind === 'Opinion' && ' · Opinion'}
                </span>
                <span className="min-w-0 flex-[1_1_320px] font-semibold underline-offset-4 hover:underline">
                  {p.title}
                </span>
              </External>
            </li>
          ))}
        </ul>
      </section>

      <CtaBand title="Want to work together, or compare notes on research?" />
    </div>
  )
}
